import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabase';

export default function CTASection() {
  const [eventCount, setEventCount] = useState<number | null>(null);
  const [ticketCount, setTicketCount] = useState<number | null>(null);
  const [socialLinksCount, setSocialLinksCount] = useState<number | null>(null);

  useEffect(() => {
    const fetchEventCount = async () => {
      const { count, error } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true });
      if (!error && count !== null) {
        setEventCount(count);
      }
    };
    
    const fetchTicketCount = async () => {
      const { count, error } = await supabase
        .from('registrations')
        .select('*', { count: 'exact', head: true });
      if (!error && count !== null) {
        setTicketCount(count);
      }
    };
    
    const fetchSocialLinksCount = async () => {
      const { count, error } = await supabase
        .from('events')
        .select('*', { count: 'exact', head: true })
        .not('social_links', 'is', null);
      if (!error && count !== null) {
        setSocialLinksCount(count);
      }
    };
    
    fetchEventCount();
    fetchTicketCount();
    fetchSocialLinksCount();
  }, []);

  const formatCount = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1).replace(/\.0$/, '')}M+`;
    if (num >= 1000) return `${(num / 1000).toFixed(1).replace(/\.0$/, '')}K+`;
    return `${num}+`;
  };

  return (
    <section className="py-16 sm:py-24 bg-gradient-to-r from-purple-600 via-purple-700 to-pink-600 relative overflow-hidden">
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-white rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-white rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center relative z-10">
        <div className="w-16 h-16 sm:w-20 sm:h-20 mx-auto mb-6 sm:mb-8 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
          <i className="ri-calendar-event-line text-4xl sm:text-5xl text-white"></i>
        </div>

        <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 px-4">
          Ready to Host Your Own Event?
        </h2>

        <p className="text-base sm:text-xl text-white/90 mb-8 sm:mb-12 max-w-2xl mx-auto px-4">
          List for free and watch your social following grow with every registration
        </p>

        <Link
          to="/create"
          className="inline-flex items-center gap-2 sm:gap-3 px-8 sm:px-12 py-4 sm:py-5 bg-white text-base sm:text-lg font-bold rounded-full hover:shadow-2xl hover:-translate-y-1 transition-all whitespace-nowrap group"
        >
          <span className="bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Create Event - It&apos;s Free
          </span>
          <i className="ri-arrow-right-line text-lg sm:text-xl text-purple-600 group-hover:translate-x-1 transition-transform"></i>
        </Link>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 mt-8 sm:mt-12 text-white/80 text-xs sm:text-sm px-4">
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-base sm:text-lg"></i>
            <span>{eventCount !== null ? formatCount(eventCount) : '10K+'} Events Created</span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-white/30"></div>
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-base sm:text-lg"></i>
            <span>{ticketCount !== null ? formatCount(ticketCount) : '500K+'} Tickets Sold</span>
          </div>
          <div className="hidden sm:block w-px h-4 bg-white/30"></div>
          <div className="flex items-center gap-2">
            <i className="ri-check-line text-base sm:text-lg"></i>
            <span>{socialLinksCount !== null ? formatCount(socialLinksCount) : '2M+'} Events with Social Links</span>
          </div>
        </div>
      </div>
    </section>
  );
}
